import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Btn, Loader, EmptyState, Field, TextInput, SelectInput } from "../components/Primitives";
import { RiskBadge, StatusBadge } from "../components/Badges";
import { useAuth } from "../lib/auth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";

const TABS = [
  { key: "discover", label: "Discover", icon: "Search" },
  { key: "findings", label: "Findings queue", icon: "Inbox" },
  { key: "inventory", label: "Inventory dashboard", icon: "LayoutDashboard" },
];

function StatCard({ label, value, tone = "primary", testId, hint }) {
  const toneColor = tone === "danger" ? "var(--danger)" : tone === "success" ? "var(--success)"
                    : tone === "warn" ? "var(--warn)" : "var(--primary)";
  return (
    <div className="surface rounded-xl p-4" style={{ border: "1px solid var(--border)" }}>
      <div className="text-[11px] font-semibold uppercase tracking-wider muted">{label}</div>
      <div data-testid={testId} className="text-2xl font-semibold mt-1" style={{ color: toneColor }}>{value}</div>
      {hint && <div className="text-[11px] muted mt-1">{hint}</div>}
    </div>
  );
}

function CategoryChip({ id, label, sensitive, confidence }) {
  return (
    <span data-testid={`category-chip-${id}`}
      className="inline-flex items-center gap-1 px-2 py-[3px] rounded-md text-[10.5px] font-medium"
      style={{
        background: sensitive ? "var(--danger-soft)" : "var(--primary-soft)",
        color: sensitive ? "var(--danger)" : "var(--primary)",
      }}>
      <Icons.Tag className="w-3 h-3" />
      {label}
      {confidence && <span className="opacity-60 text-[10px]">· {Math.round(confidence * 100)}%</span>}
    </span>
  );
}

export default function DataDiscovery() {
  const { user } = useAuth();
  const canApprove = ["super_admin", "org_admin", "dpo"].includes(user?.role);

  const [tab, setTab] = useState("discover");
  const [connectors, setConnectors] = useState([]);
  const [findings, setFindings] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [findingsFilter, setFindingsFilter] = useState({ status: "pending", risk: "" });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [scanningConn, setScanningConn] = useState("");
  const [entityForScan, setEntityForScan] = useState("");
  const [selectedConnectorId, setSelectedConnectorId] = useState("");
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveForm, setApproveForm] = useState({});
  const [showFinding, setShowFinding] = useState(null);
  const fileRef = useRef(null);

  const loadConnectors = async () => {
    try {
      const { data } = await api.get("/connectors");
      setConnectors(data.items || []);
    } catch { /* silent */ }
  };
  const loadFindings = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (findingsFilter.status) q.set("status", findingsFilter.status);
      if (findingsFilter.risk) q.set("risk", findingsFilter.risk);
      const { data } = await api.get(`/data-discovery/findings?${q.toString()}`);
      setFindings(data.items || []);
    } catch { toast.error("Failed to load findings"); }
    setLoading(false);
  };
  const loadDashboard = async () => {
    try {
      const { data } = await api.get("/data-discovery/dashboard");
      setDashboard(data);
    } catch { setDashboard(null); }
  };
  useEffect(() => { loadConnectors(); loadFindings(); loadDashboard(); }, []);
  useEffect(() => { loadFindings();   }, [findingsFilter]);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/data-discovery/scan/upload?use_ai=true", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Scanned "${data.dataset_name}" — ${data.pii_column_count} PII columns · ${data.risk_rating} risk`);
      setShowFinding(data);
      await loadFindings();
      await loadDashboard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const doConnectorScan = async () => {
    if (!selectedConnectorId) { toast.error("Pick a connector first"); return; }
    setScanningConn(selectedConnectorId);
    try {
      const { data } = await api.post(`/data-discovery/scan/connector/${selectedConnectorId}`, {
        entity: entityForScan || undefined,
        limit: 200, use_ai: true,
      });
      toast.success(`Scanned ${data.count} dataset(s) from connector`);
      await loadFindings();
      await loadDashboard();
      setTab("findings");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connector scan failed");
    }
    setScanningConn("");
  };

  const openApprove = (f) => {
    setApproveTarget(f);
    setApproveForm({
      dataset_name: f.dataset_name || f.source_ref,
      asset_type: f.suggested_asset_type || "database",
      retention_period_days: f.suggested_retention_days || 730,
      risk_rating: f.risk_rating,
      purpose: f.suggested_purpose || "",
      lawful_basis: f.suggested_lawful_basis || "consent",
      department: "",
      business_owner: "",
      technical_owner: "",
      hosting_location: "",
      country: "India",
      notes: "",
    });
  };

  const submitApprove = async () => {
    if (!approveTarget) return;
    try {
      const { data } = await api.post(`/data-discovery/findings/${approveTarget.id}/approve`, approveForm);
      toast.success(`Added to inventory: ${data.data_asset.name}`);
      setApproveTarget(null);
      await loadFindings();
      await loadDashboard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    }
  };

  const rejectFinding = async (f) => {
    const reason = window.prompt("Reason for rejection (optional):", "");
    try {
      await api.post(`/data-discovery/findings/${f.id}/reject`, { reason });
      toast.success("Rejected");
      await loadFindings();
    } catch { toast.error("Reject failed"); }
  };

  const activeConnectors = useMemo(
    () => connectors.filter((c) => c.enabled && (c.last_sync_records || 0) > 0),
    [connectors],
  );
  const currentConnector = connectors.find((c) => c.id === selectedConnectorId);
  const entities = currentConnector?.definition?.sync_entities || [];

  return (
    <div>
      <PageHeader
        title="Data Discovery & Personal Data Inventory"
        kicker="Discovery"
        description="Identify, classify and map personal data across systems. Upload files (CSV/Excel/PDF/DOCX/JSON) or scan data pulled by enterprise connectors — the platform detects PII, sensitive categories, children's data, and proposes retention, controls and gaps for compliance-officer approval."
        testId="data-discovery-header"
        actions={
          <>
            <Btn variant="secondary" onClick={() => { loadFindings(); loadDashboard(); loadConnectors(); }} data-testid="dd-refresh">
              <Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Btn>
          </>
        }
      />

      {/* Tabs */}
      <div className="mb-5 flex items-center gap-1 surface rounded-lg p-1 w-fit" style={{ border: "1px solid var(--border)" }}>
        {TABS.map((t) => {
          const Icon = Icons[t.icon] || Icons.Circle;
          return (
            <button
              key={t.key}
              data-testid={`dd-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className="px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-1.5"
              style={{
                background: tab === t.key ? "var(--primary-soft)" : "transparent",
                color: tab === t.key ? "var(--primary)" : "var(--muted)",
              }}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
              {t.key === "findings" && dashboard?.pending_findings > 0 && (
                <span className="ml-1 px-1.5 py-[1px] rounded-full text-[9px] font-bold"
                  style={{ background: "var(--warn)", color: "white" }}>
                  {dashboard.pending_findings}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "discover" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Upload card */}
          <div className="surface rounded-xl p-6" style={{ border: "1px solid var(--border)" }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                <Icons.Upload className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold ink">Upload a file</div>
                <div className="text-[12px] muted">CSV · Excel · PDF · DOCX · JSON (up to 20 MB)</div>
              </div>
            </div>
            <div className="rounded-lg p-8 text-center"
              style={{ border: "2px dashed var(--border-strong)", background: "var(--bg-2)" }}>
              <Icons.FileSearch className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--primary)" }} />
              <div className="text-[13px] ink font-medium mb-1">Drop a file or click to browse</div>
              <div className="text-[11px] muted mb-3">
                Column headers + sample values are analysed against 15 personal-data categories using deterministic patterns and Claude Sonnet 4.5.
              </div>
              <input
                ref={fileRef}
                data-testid="dd-upload-input"
                type="file"
                accept=".csv,.xlsx,.xls,.pdf,.docx,.json,.txt"
                onChange={(e) => doUpload(e.target.files?.[0])}
                className="hidden"
              />
              <Btn onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="dd-upload-btn">
                <Icons.Upload className="w-3.5 h-3.5" /> {uploading ? "Scanning…" : "Choose file"}
              </Btn>
            </div>
          </div>

          {/* Connector scan card */}
          <div className="surface rounded-xl p-6" style={{ border: "1px solid var(--border)" }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                <Icons.Plug className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold ink">Scan a connector</div>
                <div className="text-[12px] muted">Analyse data already pulled by an enterprise connector</div>
              </div>
            </div>
            {activeConnectors.length === 0 ? (
              <EmptyState
                title="No connector data yet"
                hint="Go to Enterprise Connectors, configure a source, and run 'Sync now' before scanning here."
                action={<a href="/app/connectors" className="text-[12px] font-semibold" style={{ color: "var(--primary)" }}>Go to Connectors →</a>}
              />
            ) : (
              <div className="grid gap-3">
                <Field label="Connector">
                  <SelectInput
                    data-testid="dd-connector-select"
                    value={selectedConnectorId}
                    onChange={(e) => { setSelectedConnectorId(e.target.value); setEntityForScan(""); }}
                  >
                    <option value="">-- Pick a connector --</option>
                    {activeConnectors.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} — {c.last_sync_records || 0} records
                      </option>
                    ))}
                  </SelectInput>
                </Field>
                {entities.length > 0 && (
                  <Field label="Entity (optional)">
                    <SelectInput
                      data-testid="dd-entity-select"
                      value={entityForScan}
                      onChange={(e) => setEntityForScan(e.target.value)}
                    >
                      <option value="">All entities</option>
                      {entities.map((e) => <option key={e.key} value={e.key}>{e.key}</option>)}
                    </SelectInput>
                  </Field>
                )}
                <Btn onClick={doConnectorScan} disabled={!selectedConnectorId || !!scanningConn} data-testid="dd-scan-connector-btn">
                  <Icons.ScanSearch className="w-3.5 h-3.5" /> {scanningConn ? "Scanning…" : "Scan connector data"}
                </Btn>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "findings" && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <SelectInput
              data-testid="findings-status-filter"
              value={findingsFilter.status}
              onChange={(e) => setFindingsFilter({ ...findingsFilter, status: e.target.value })}
              style={{ maxWidth: 180 }}
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </SelectInput>
            <SelectInput
              data-testid="findings-risk-filter"
              value={findingsFilter.risk}
              onChange={(e) => setFindingsFilter({ ...findingsFilter, risk: e.target.value })}
              style={{ maxWidth: 180 }}
            >
              <option value="">All risks</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </SelectInput>
          </div>

          {loading ? <Loader /> : findings.length === 0 ? (
            <EmptyState
              title="No findings match this filter"
              hint="Try switching status to 'All' or run a discovery scan from the Discover tab."
            />
          ) : (
            <div className="grid gap-3" data-testid="findings-list">
              {findings.map((f) => (
                <div key={f.id} className="surface rounded-xl p-4"
                  style={{ border: "1px solid var(--border)" }}
                  data-testid={`finding-${f.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => setShowFinding(f)}
                          className="font-semibold ink text-[14px] hover:underline text-left"
                          data-testid={`finding-open-${f.id}`}>
                          {f.dataset_name || f.source_ref}
                        </button>
                        <RiskBadge level={f.risk_rating} />
                        <StatusBadge status={f.status} />
                        {f.has_children_data && (
                          <span className="inline-flex items-center gap-1 px-2 py-[3px] rounded-md text-[10.5px] font-semibold"
                            style={{ background: "var(--danger-soft)", color: "var(--danger)" }}>
                            <Icons.Baby className="w-3 h-3" /> Children&apos;s data
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] muted mt-1 flex items-center gap-3 flex-wrap">
                        <span><Icons.FileText className="w-3 h-3 inline mr-1" />{f.kind} · {f.source_type}</span>
                        <span><Icons.Columns3 className="w-3 h-3 inline mr-1" />{f.pii_column_count}/{f.total_column_count} PII columns</span>
                        <span><Icons.ShieldAlert className="w-3 h-3 inline mr-1" />{f.sensitive_count} sensitive</span>
                        <span><Icons.Calendar className="w-3 h-3 inline mr-1" />{new Date(f.created_at).toLocaleString()}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                        {(f.detected_category_ids || []).slice(0, 8).map((id) => {
                          const cat = (f.columns || []).flatMap((c) => c.detected_categories || [])
                            .find((c) => c.id === id) || { id, label: id, sensitive: false };
                          return <CategoryChip key={id} id={id} label={cat.label} sensitive={cat.sensitive} />;
                        })}
                        {(f.detected_category_ids || []).length > 8 && (
                          <span className="text-[10.5px] muted">+{f.detected_category_ids.length - 8} more</span>
                        )}
                      </div>
                      {f.ai_narrative && (
                        <div className="mt-2 text-[12px] muted italic line-clamp-2">
                          <Icons.Sparkles className="w-3 h-3 inline mr-1" style={{ color: "var(--primary)" }} />
                          {f.ai_narrative}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Btn variant="secondary" size="sm" onClick={() => setShowFinding(f)} data-testid={`finding-view-${f.id}`}>
                        <Icons.Eye className="w-3.5 h-3.5" /> Details
                      </Btn>
                      {canApprove && f.status === "pending" && (
                        <>
                          <Btn size="sm" onClick={() => openApprove(f)} data-testid={`finding-approve-${f.id}`}>
                            <Icons.CheckCircle2 className="w-3.5 h-3.5" /> Approve
                          </Btn>
                          <Btn variant="secondary" size="sm" onClick={() => rejectFinding(f)} data-testid={`finding-reject-${f.id}`}>
                            <Icons.XCircle className="w-3.5 h-3.5" /> Reject
                          </Btn>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "inventory" && (
        !dashboard ? <Loader /> : (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <StatCard label="Total data assets" value={dashboard.total_assets} testId="stat-total-assets" />
              <StatCard label="Sensitive risk"
                value={(dashboard.by_risk?.high || 0) + (dashboard.by_risk?.critical || 0)}
                tone="danger" testId="stat-sensitive"
                hint={`${dashboard.by_risk?.critical || 0} critical, ${dashboard.by_risk?.high || 0} high`} />
              <StatCard label="Children's data assets" value={dashboard.children_data_assets}
                tone="warn" testId="stat-children" />
              <StatCard label="Pending discovery" value={dashboard.pending_findings || 0}
                tone="warn" testId="stat-pending" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
              <StatCard label="Without owner" value={dashboard.without_owner} tone="warn" testId="stat-no-owner" />
              <StatCard label="Without defined purpose" value={dashboard.without_purpose} tone="warn" testId="stat-no-purpose" />
              <StatCard label="Without retention policy" value={dashboard.without_retention} tone="warn" testId="stat-no-retention" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
                <div className="text-[13px] font-semibold ink mb-3">Assets by risk rating</div>
                <div className="space-y-2">
                  {["critical", "high", "medium", "low"].map((k) => {
                    const v = dashboard.by_risk?.[k] || 0;
                    const total = dashboard.total_assets || 1;
                    const pct = Math.round((v / total) * 100);
                    return (
                      <div key={k} className="flex items-center gap-2">
                        <div className="w-16 text-[11px] muted capitalize">{k}</div>
                        <div className="flex-1 h-2.5 rounded-full" style={{ background: "var(--bg-2)" }}>
                          <div className="h-full rounded-full transition-all"
                            style={{
                              width: `${pct}%`,
                              background: k === "critical" ? "var(--danger)"
                                : k === "high" ? "#F97316"
                                : k === "medium" ? "var(--warn)" : "var(--success)",
                            }} />
                        </div>
                        <div className="w-14 text-right text-[12px] ink font-medium">{v} ({pct}%)</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="surface rounded-xl p-5" style={{ border: "1px solid var(--border)" }}>
                <div className="text-[13px] font-semibold ink mb-3">Assets by department</div>
                {(dashboard.by_department || []).filter(d => d.name !== "Unassigned" || d.count > 0).length === 0 ? (
                  <div className="text-[12px] muted">No department assigned yet. Set it during approval.</div>
                ) : (
                  <div className="space-y-2">
                    {dashboard.by_department.map((d) => (
                      <div key={d.name} className="flex items-center gap-2">
                        <div className="flex-1 text-[12px] ink truncate">{d.name}</div>
                        <div className="text-[12px] muted">{d.count}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      )}

      {/* Finding detail modal */}
      <Dialog open={!!showFinding} onOpenChange={(v) => !v && setShowFinding(null)}>
        <DialogContent className="max-w-3xl">
          {showFinding && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {showFinding.dataset_name || showFinding.source_ref}
                  <RiskBadge level={showFinding.risk_rating} />
                </DialogTitle>
                <DialogDescription>
                  {showFinding.kind} · {showFinding.row_count} rows · {showFinding.pii_column_count}/{showFinding.total_column_count} personal-data columns
                </DialogDescription>
              </DialogHeader>
              <div className="max-h-[62vh] overflow-y-auto space-y-4">
                {showFinding.ai_narrative && (
                  <div className="rounded-lg p-3 text-[13px]" style={{ background: "var(--primary-soft)", color: "var(--primary)" }}>
                    <Icons.Sparkles className="w-4 h-4 inline mr-1" />
                    {showFinding.ai_narrative}
                  </div>
                )}
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider muted mb-2">Columns</div>
                  <div className="grid gap-1.5" data-testid="finding-columns">
                    {(showFinding.columns || []).map((c, i) => (
                      <div key={i} className="p-2 rounded-md" style={{ border: "1px solid var(--border)" }}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="font-mono text-[12px] ink">{c.name}</div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {(c.detected_categories || []).map((d) =>
                              <CategoryChip key={d.id} id={d.id} label={d.label} sensitive={d.sensitive} confidence={d.confidence} />
                            )}
                            {!c.is_personal_data && <span className="text-[10.5px] muted">no PII</span>}
                          </div>
                        </div>
                        {c.samples?.length > 0 && (
                          <div className="text-[10.5px] mono muted mt-1 truncate">
                            e.g. {c.samples.slice(0, 3).join(" · ")}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {(showFinding.suggested_gaps?.length > 0 || showFinding.suggested_controls?.length > 0) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {showFinding.suggested_controls?.length > 0 && (
                      <div className="p-3 rounded-lg" style={{ background: "var(--success-soft)" }}>
                        <div className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--success)" }}>
                          Recommended controls
                        </div>
                        <ul className="text-[12px] ink space-y-1 list-disc list-inside">
                          {showFinding.suggested_controls.map((c, i) => <li key={i}>{c}</li>)}
                        </ul>
                      </div>
                    )}
                    {showFinding.suggested_gaps?.length > 0 && (
                      <div className="p-3 rounded-lg" style={{ background: "var(--warn-soft)" }}>
                        <div className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--warn)" }}>
                          Compliance gaps
                        </div>
                        <ul className="text-[12px] ink space-y-1 list-disc list-inside">
                          {showFinding.suggested_gaps.map((c, i) => <li key={i}>{c}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Btn variant="secondary" onClick={() => setShowFinding(null)} data-testid="finding-detail-close">Close</Btn>
                {canApprove && showFinding.status === "pending" && (
                  <Btn onClick={() => { openApprove(showFinding); setShowFinding(null); }} data-testid="finding-detail-approve">
                    <Icons.CheckCircle2 className="w-3.5 h-3.5" /> Approve & add to inventory
                  </Btn>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Approve modal */}
      <Dialog open={!!approveTarget} onOpenChange={(v) => !v && setApproveTarget(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Approve & add to Data Inventory</DialogTitle>
            <DialogDescription>
              Adjust asset attributes before promoting to the canonical inventory. Suggested gaps will be created as tasks for the DPO.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
            <Field label="Dataset name" className="col-span-2">
              <TextInput data-testid="approve-name"
                value={approveForm.dataset_name || ""}
                onChange={(e) => setApproveForm({ ...approveForm, dataset_name: e.target.value })} />
            </Field>
            <Field label="Asset type">
              <SelectInput data-testid="approve-asset-type" value={approveForm.asset_type}
                onChange={(e) => setApproveForm({ ...approveForm, asset_type: e.target.value })}>
                {["application", "database", "saas", "cloud", "website", "mobile", "api", "spreadsheet", "legacy", "file_store"].map((t) =>
                  <option key={t} value={t}>{t}</option>)}
              </SelectInput>
            </Field>
            <Field label="Risk rating">
              <SelectInput data-testid="approve-risk" value={approveForm.risk_rating}
                onChange={(e) => setApproveForm({ ...approveForm, risk_rating: e.target.value })}>
                {["low", "medium", "high", "critical"].map((r) => <option key={r} value={r}>{r}</option>)}
              </SelectInput>
            </Field>
            <Field label="Business owner">
              <TextInput data-testid="approve-business-owner" value={approveForm.business_owner || ""}
                onChange={(e) => setApproveForm({ ...approveForm, business_owner: e.target.value })} />
            </Field>
            <Field label="Department">
              <TextInput data-testid="approve-department" value={approveForm.department || ""}
                onChange={(e) => setApproveForm({ ...approveForm, department: e.target.value })}
                placeholder="e.g. HR / Sales / Engineering" />
            </Field>
            <Field label="Technical owner">
              <TextInput data-testid="approve-technical-owner" value={approveForm.technical_owner || ""}
                onChange={(e) => setApproveForm({ ...approveForm, technical_owner: e.target.value })} />
            </Field>
            <Field label="Hosting location">
              <TextInput data-testid="approve-hosting" value={approveForm.hosting_location || ""}
                onChange={(e) => setApproveForm({ ...approveForm, hosting_location: e.target.value })} />
            </Field>
            <Field label="Country">
              <TextInput data-testid="approve-country" value={approveForm.country || "India"}
                onChange={(e) => setApproveForm({ ...approveForm, country: e.target.value })} />
            </Field>
            <Field label="Retention (days)">
              <TextInput type="number" data-testid="approve-retention"
                value={approveForm.retention_period_days || 730}
                onChange={(e) => setApproveForm({ ...approveForm, retention_period_days: Number(e.target.value) })} />
            </Field>
            <Field label="Lawful basis">
              <SelectInput data-testid="approve-lawful-basis" value={approveForm.lawful_basis || "consent"}
                onChange={(e) => setApproveForm({ ...approveForm, lawful_basis: e.target.value })}>
                {["consent", "legitimate_use", "legal_obligation", "employment", "medical_public_interest", "state_function", "other"].map((r) =>
                  <option key={r} value={r}>{r}</option>)}
              </SelectInput>
            </Field>
            <Field label="Purpose of processing" className="col-span-2">
              <TextInput data-testid="approve-purpose" value={approveForm.purpose || ""}
                onChange={(e) => setApproveForm({ ...approveForm, purpose: e.target.value })}
                placeholder="e.g. Customer onboarding + KYC verification" />
            </Field>
            <Field label="Approval notes" className="col-span-2">
              <TextInput data-testid="approve-notes" value={approveForm.notes || ""}
                onChange={(e) => setApproveForm({ ...approveForm, notes: e.target.value })} />
            </Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setApproveTarget(null)} data-testid="approve-cancel">Cancel</Btn>
            <Btn onClick={submitApprove} data-testid="approve-submit">
              <Icons.CheckCircle2 className="w-3.5 h-3.5" /> Approve & add
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
